import { Link } from "react-router-dom";
import { FadeUp } from "../_shared";
import HudCorners from "./HudCorners";

function Block({ label, body }) {
  return (
    <div>
      <div className="iw-overline text-[0.55rem] mb-2">{label}</div>
      <p className="text-sm md:text-base text-foreground/80 leading-relaxed">{body}</p>
    </div>
  );
}

/**
 * Compact PT.0X patent-pending technology card.
 * Image + name + one-liner up front; the deep What/How/Significance content
 * lives inside a collapsed <details> so it stays crawlable without the wall of text.
 */
export default function PatentTechCard({ tech, index }) {
  const code = `PT.0${index + 1}`;
  return (
    <FadeUp delay={index * 0.05}>
      <div
        data-testid={`patent-${index + 1}`}
        className="flex flex-col h-full bg-card border border-border"
      >
        <div className="iw-scan-sweep relative aspect-[16/9] overflow-hidden bg-foreground/5">
          <img
            src={tech.image}
            alt={`${tech.name} — patent pending technology by Integrated Wellness`}
            className="w-full h-full object-cover"
            loading="lazy"
            style={{ filter: "brightness(1.05) contrast(1.05) saturate(1.1)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#11296B]/45 to-transparent pointer-events-none" />
          <HudCorners offset={10} size={10} />
          <span className="absolute top-3 left-3 border border-white/40 bg-[#11296B]/60 backdrop-blur-sm px-2.5 py-1 font-mono text-[0.6rem] uppercase tracking-[0.22em] text-white">
            {code}
          </span>
        </div>

        <div className="flex flex-col flex-1 p-6 md:p-7">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[0.58rem] uppercase tracking-[0.2em] text-accent font-semibold">
              {tech.badge || "Patent Pending"}
            </span>
            {tech.docket && (
              <span className="font-mono text-[0.55rem] uppercase tracking-[0.18em] text-muted-foreground">
                · {tech.docket}
              </span>
            )}
          </div>
          <h3 className="mt-3 font-display text-lg md:text-xl font-bold leading-tight tracking-tight text-primary">
            {tech.name}
          </h3>
          <p className="mt-2.5 text-sm md:text-base text-foreground/70 leading-relaxed flex-1">
            {tech.sub}
          </p>

          <details className="group mt-4 border-t border-border pt-1">
            <summary
              data-testid={`patent-${index + 1}-details-toggle`}
              className="cursor-pointer list-none py-2.5 flex items-center justify-between"
            >
              <span className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-primary font-semibold">
                Technical Detail
              </span>
              <span aria-hidden className="text-accent font-bold text-base leading-none group-open:rotate-45 transition-transform">
                +
              </span>
            </summary>
            <div className="pb-4 space-y-4">
              <Block label="What It Does" body={tech.what} />
              <Block label="How It Works" body={tech.how} />
              <Block label="Clinical Significance" body={tech.sig} />
            </div>
          </details>

          {tech.href && (
            <Link
              to={tech.href}
              data-testid={`patent-${index + 1}-cta`}
              className="iw-btn-ghost mt-3 inline-flex items-center gap-2 px-5 py-2.5 text-[0.62rem] w-fit"
            >
              {tech.hrefLabel || "Learn more"} <span aria-hidden>→</span>
            </Link>
          )}
        </div>
      </div>
    </FadeUp>
  );
}
